import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  Checkbox,
  Divider,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';
import { useState } from 'react';

import { LogoutLink } from 'src/components/LogoutLink';
import translations from 'src/locales.vocab';
import type { AcceptAction, RejectAction } from 'src/types/api';
import type { Scope } from 'src/types/scope';
export interface Props {
  /**
   * Prevent candidates from making additional actions if they're in the middle of an API request.
   */
  loading: { accept: boolean; reject: boolean };
  /**
   * The organization that is hiring for the position.
   *
   * This is only available where the integration partner has sent us a
   * legitimate `advertiser_id` search parameter.
   *
   * {@link https://devportal.seek.com.au/apply-with-seek/api-methods/authorization-code}
   */
  hirerName: string | null;

  /**
   * The integration partner that is facilitating the application flow.
   */
  partnerName: string;

  /**
   * The requested scope for the access request.
   */
  scope: Scope;

  /**
   * The flag for the Shareable Profile experiment
   */
  shouldShowShareableProfile: boolean;

  /**
   * The email address of the logged-in candidate.
   */
  userEmail: string;

  /**
   * The callback to invoke when the user accepts the prefill request.
   */
  onAccept: (props: {
    action: AcceptAction;
    candidateWillingToShareProfile: boolean;
    shareableProfileExperiment: boolean;
  }) => void;

  /**
   * The callback to invoke when the user rejects the prefill request.
   */
  onReject: (props: {
    action: RejectAction;
    candidateWillingToShareProfile: boolean;
    shareableProfileExperiment: boolean;
  }) => void;
}

export const ConsentPrompt = ({
  loading,
  hirerName,
  partnerName,
  scope,
  shouldShowShareableProfile,
  userEmail,
  onAccept,
  onReject,
}: Props) => {
  const { t } = useTranslations(translations);
  const [shareableChecked, setShareableChecked] = useState(false);
  const [candidateWillingToShareProfile, setCandidateWillingToShareProfile] =
    useState(false);
  const shareableProfileExperiment = shouldShowShareableProfile;

  return (
    <Stack space="xlarge">
      <Stack space="medium">
        <Text component="h1" size="large">
          {t('Hi {userEmail}', {
            userEmail,
          })}
        </Text>

        <LogoutLink>{t('Not you?')}</LogoutLink>
      </Stack>
      <Stack space="large">
        <Text>
          {hirerName
            ? `By continuing you allow ${hirerName} via ${partnerName} access to the information in your SEEK Profile to pre-fill the job application.`
            : `By continuing you allow ${partnerName} access to the information in your SEEK Profile to pre-fill the job application.`}
        </Text>
        <Text>
          {scope === 'r_profile_apply'
            ? 'This includes your career history, skills, default resumé, email address and phone number.'
            : 'This includes your career history, skills, email address and phone number.'}
        </Text>
        {shareableProfileExperiment ? (
          <>
            <Text>
              <Strong>Additional options</Strong>
            </Text>
            <Box
              boxShadow="borderNeutralLight"
              padding="large"
              background="surface"
              borderRadius="xlarge"
            >
              <Checkbox
                id="Shareable Profile"
                label="Share a link to your SEEK Profile"
                description="Let anyone with the link to your profile access the most up-to-date info."
                checked={shareableChecked}
                onChange={(event) => {
                  setShareableChecked(event.currentTarget.checked);
                  setCandidateWillingToShareProfile(true);
                }}
              >
                <Box paddingTop="medium">
                  <Stack space="large">
                    <Divider />
                    <Stack space="medium">
                      <Text weight="medium">Thank you for your interest</Text>
                      <Text>
                        Sharing your SEEK Profile is something we’re still
                        building. We’ll let you know when you can share your
                        profile with anyone.
                      </Text>
                    </Stack>
                  </Stack>
                </Box>
              </Checkbox>
            </Box>
          </>
        ) : null}
      </Stack>

      <Actions>
        <Button
          onClick={() =>
            onAccept({
              action: 'accept',
              candidateWillingToShareProfile,
              shareableProfileExperiment,
            })
          }
          loading={loading.accept}
        >
          {t('Allow access')}
        </Button>
        <Button
          onClick={() =>
            onReject({
              action: 'reject',
              candidateWillingToShareProfile,
              shareableProfileExperiment,
            })
          }
          variant="transparent"
          loading={loading.reject}
        >
          {t('Not now')}
        </Button>
      </Actions>
    </Stack>
  );
};
